import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

// ---------------------------------------------------------------------------
// Groups menu items by (category_id, subcategory_id || 'none') and enables
// cross-group drag-and-drop. On successful drop, calls `onMoveItem` with
// { id, category_id, subcategory_id, orderedIds } so the parent can persist.
// ---------------------------------------------------------------------------

const NO_SUB = "__no_sub__";
const GROUP_PREFIX = "group::";

export function groupKey(categoryId, subcategoryId) {
  return `${categoryId}:${subcategoryId || NO_SUB}`;
}

function parseGroupKey(key) {
  const [category_id, sub] = key.split(":");
  return { category_id, subcategory_id: sub === NO_SUB ? null : sub };
}

export function buildGroups({ items, categories, subs, onlyCategoryId = null }) {
  const groups = [];
  const visibleCats = onlyCategoryId ? categories.filter((c) => c.id === onlyCategoryId) : categories;
  for (const cat of visibleCats) {
    // "Uncategorized" pseudo-group always first (even if empty, so users can
    // drop items that don't belong to a sub-category into it).
    const uncategorized = items.filter((i) => i.category_id === cat.id && !i.subcategory_id);
    groups.push({
      key: groupKey(cat.id, null),
      category: cat,
      subcategory: null,
      items: uncategorized,
    });
    const catSubs = subs.filter((s) => s.category_id === cat.id);
    for (const sub of catSubs) {
      groups.push({
        key: groupKey(cat.id, sub.id),
        category: cat,
        subcategory: sub,
        items: items.filter((i) => i.subcategory_id === sub.id && i.category_id === cat.id),
      });
    }
  }
  return groups;
}

// -- Sortable row wrapper ----------------------------------------------------
function SortableItemRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
}

// -- Group (droppable container for one (category, subcategory)) ------------
function Group({ group, renderHeader, renderItem, renderEmpty, isActiveSource }) {
  // Register the group key as its own droppable so empty groups still
  // receive drops and so drops on the header/empty area are routed correctly.
  const { setNodeRef, isOver } = useDroppable({ id: `${GROUP_PREFIX}${group.key}` });
  const ring = isOver ? "ring-2 ring-brand-orange/40 bg-brand-orange/5" : isActiveSource ? "" : "";

  return (
    <div className={`rounded-lg transition-colors ${ring}`} data-testid={`group-${group.key}`}>
      {renderHeader(group)}
      <SortableContext items={group.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className="min-h-[56px]">
          {group.items.length === 0
            ? renderEmpty(group)
            : group.items.map((it) => (
                <SortableItemRow key={it.id} id={it.id}>
                  {(ctx) => renderItem(it, group, ctx)}
                </SortableItemRow>
              ))}
        </div>
      </SortableContext>
    </div>
  );
}

// -- Main component ---------------------------------------------------------
export function GroupedItemsDnD({
  items,
  categories,
  subs,
  filterCategoryId,
  renderGroupHeader,
  renderItem,
  renderEmptyGroup,
  onMoveItem,
}) {
  const [activeId, setActiveId] = useState(null);

  const groups = useMemo(
    () => buildGroups({ items, categories, subs, onlyCategoryId: filterCategoryId === "all" ? null : filterCategoryId }),
    [items, categories, subs, filterCategoryId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findGroupOf = (itemId) => groups.find((g) => g.items.some((i) => i.id === itemId));
  const findGroupByKey = (key) => groups.find((g) => g.key === key);

  const findTargetGroup = (overId) => {
    if (!overId) return null;
    if (typeof overId === "string" && overId.startsWith(GROUP_PREFIX)) {
      return findGroupByKey(overId.slice(GROUP_PREFIX.length));
    }
    // over is another item → use its group
    return findGroupOf(overId);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const sourceGroup = findGroupOf(active.id);
    const targetGroup = findTargetGroup(over.id);
    if (!sourceGroup || !targetGroup) return;

    const activeItem = sourceGroup.items.find((i) => i.id === active.id);
    if (!activeItem) return;

    let orderedIds;
    if (sourceGroup.key === targetGroup.key) {
      // Same-group reorder
      if (active.id === over.id) return;
      const oldIdx = sourceGroup.items.findIndex((i) => i.id === active.id);
      const newIdx = sourceGroup.items.findIndex((i) => i.id === over.id);
      if (oldIdx === newIdx) return;
      const reordered = arrayMove(sourceGroup.items, oldIdx, newIdx);
      orderedIds = reordered.map((i) => i.id);
    } else {
      // Cross-group move
      const filteredTarget = targetGroup.items.filter((i) => i.id !== active.id);
      let insertIdx;
      if (typeof over.id === "string" && over.id.startsWith(GROUP_PREFIX)) {
        insertIdx = filteredTarget.length; // dropped on group container → append
      } else {
        insertIdx = filteredTarget.findIndex((i) => i.id === over.id);
        if (insertIdx === -1) insertIdx = filteredTarget.length;
      }
      filteredTarget.splice(insertIdx, 0, activeItem);
      orderedIds = filteredTarget.map((i) => i.id);
    }

    const dest = parseGroupKey(targetGroup.key);
    onMoveItem({
      id: activeItem.id,
      sourceGroupKey: sourceGroup.key,
      targetGroupKey: targetGroup.key,
      category_id: dest.category_id,
      subcategory_id: dest.subcategory_id,
      orderedIdsInTargetGroup: orderedIds,
    });
  };

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {groups.map((g) => (
          <Group
            key={g.key}
            group={g}
            renderHeader={renderGroupHeader}
            renderItem={renderItem}
            renderEmpty={renderEmptyGroup}
            isActiveSource={activeItem && (activeItem.category_id === g.category.id) && ((activeItem.subcategory_id || null) === (g.subcategory?.id || null))}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className="bg-white border border-brand-orange/40 rounded-lg shadow-xl px-4 py-3 text-sm font-semibold text-foreground/90 flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            {activeItem.name}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
